import { CustomElement, OverrideCustomElementConfig } from "@cervval.js/client-html5-core/src/components/customElement.decorator";
import { Event, EventHandler, NoBubblesEventInit } from "@cervval.js/client-html5-core/src/components/event.decorator";
import { TypedHtmlElements } from "@cervval.js/client-html5-core/src/components/typedHtmlElements.decorator";
import { TypedWebComponentOptions, WebComponentOptions } from "@cervval.js/client-html5-core/src/components/typedWebComponentOptions.decorator";
import { WebComponent } from "@cervval.js/client-html5-core/src/components/webComponent";
import { toggleClass, toggleVisible } from "@cervval.js/client-html5-core/src/tools/css.tools";
import { ALIGN_ITEMS, FLEX_ITEM_AUTO } from "@cervval.js/client-html5-core/src/tools/cssClasses.tools";
import { AcceptedHtmlElementContent, KeyboardInput, createHtmlElement, findElementByRef, setHTMLElementContent, toggleAttribute } from "@cervval.js/client-html5-core/src/tools/dom.tools";
import { isNumber, isNumeric } from "@cervval.js/common/src/tools/number.tools";
import { ValueOrFunction, getVOFValue } from "@cervval.js/common/src/tools/object.tools";

export enum InputSize {
    SMALL = "small",
    NORMAL = "normal",
    LARGE = "large"
}

export type AcceptInputValueType = string | number;

export interface InputClearEvent {

    /** Value of input who has been cleared */
    valueCleared: AcceptInputValueType | undefined;
}

export interface InputOptions<InputValueType> {

    /**
     * Initial value of input
     */
    value?: InputValueType;

    /** 
     * Placeholder text
     * @default ""
     */
    placeholderText?: string;

    /**
     * Disable input, value can't be edited by user
     * @default false
     */
    disabled: ValueOrFunction<boolean>;

    /**
     * Type of input
     * Accepts: text | number | color | password | time
     * @default text
     */
    inputType: InputType;

    /**
     * Html input name
     */
    inputName?: string;

    /** Prefix of input */
    inputPrefixText?: AcceptedHtmlElementContent;

    /** Suffix of input */
    inputSuffixText?: AcceptedHtmlElementContent;

    /** Enable form-control bootstrap class on input */
    enableFormControl: boolean;

    /** 
     * Readonly options for input
     * In readonly mode, user can't edit input value, but isn't like disabled display
     * enable: Add the readonly boolean attribute on an input to prevent modification of the input’s value.
     * plaintText: If you want to have <input readonly> elements in your form styled as plain text, use plaintext to remove the default form field styling and preserve the correct margin and padding.
     */
    readonly: { enable: boolean, plainText?: boolean };

    /** 
     * Textarea option for input
     * textarea: Display input as textarea, hide <input> and display <textarea> div
     * rows: Number of rows for textarea
     */
    textarea: { enable: boolean, rows?: number };

    /** Enable floating label for placeholder */
    floatingLabel: {
        enable: boolean,
        text: string // FIXME this text should not override the placeholderText
    };

    /**
     * Size of input, accepts 3 types of values 
     * 
     * InputSize:
     * SMALL: form-control-sm class from bootstrap
     * NORMAL: default size
     * LARGE: form-control-lg class from bootstrap
     *
     */
    size: InputSize;

    /**
     * Input width dimension:
     * string: accepts css dimension value (px, em, vh, vw, %, etc.)
     * @default undefined
     */
    width?: string;

    /** Enable erase button to remove input content */
    enableClearButton?: boolean;

    /**
     * If inputType is equal to "range", this attribute set some options for the range input
     */
    rangeOptions?: {
        /**
         * Minimal value for range. Default 0
         */
        min?: number,
        /**
         * Maximal value for range. Default 100
         */
        max?: number,
        /**
         * Incremental step between each range value. Default 1
         */
        step?: number | "any"
    };
    /**
     * !! Warning if you want to choose the number after the coma use rangeOtions.step : 0.0..01
     */
    useFloat?: boolean;

    /** by default all key events are captured and not propagated outside the component (except ENTER and ESCAPE and TAB) */
    doNotCaptureKeyEvents?: boolean | KeyboardInput[];
}

export const defaultInputOptions: InputOptions<AcceptInputValueType> = {

    /**
     * Disable input, value can't be edited by user
     * @default false
     */
    disabled: false,

    /** 
     * Type of input
     * Accepts: text | number | color | password | time | range
     * @default text
     */
    inputType: "text",

    /** Enable form-control bootstrap class on input */
    enableFormControl: true,

    /** 
     * Readonly options for input
     * In readonly mode, user can't edit input value, but isn't like disabled display
     * enable: Add the readonly boolean attribute on an input to prevent modification of the input’s value.
     * plaintText: If you want to have <input readonly> elements in your form styled as plain text, use plaintext to remove the default form field styling and preserve the correct margin and padding.
     */
    readonly: {
        enable: false,
        plainText: false
    },

    /** 
     * Textarea option for input
     * textarea: Display input as textarea, hide <input> and display <textarea> div
     * rows: Number of rows for textarea
     */
    textarea: {
        enable: false,
        rows: 2
    },

    /** Enable floating label for placeholder */
    floatingLabel: {
        enable: false,
        text: ""
    },

    /**
     * Size of input, accepts 3 types of values 
     * 
     * InputSize:
     * SMALL: form-control-sm class from bootstrap
     * NORMAL: default size
     * LARGE: form-control-lg class from bootstrap
     *
     */
    size: InputSize.NORMAL,

    /** by default all key events are captured and not propagated outside the component (except ENTER and ESCAPE and TAB) */
    doNotCaptureKeyEvents: [KeyboardInput.ENTER, KeyboardInput.ESCAPE, KeyboardInput.TAB]
};

export type InputType = "text" | "number" | "color" | "password" | "time" | "range" | "tel";

const READONLY_ATTRIBUTE_KEY = "readonly";

const READONLY_PLAIN_TEXT = "form-control-plaintext";
const FORM_CONTROL = "form-control";
const FORM_CONTROL_COLOR = "form-control-color";
const FORM_RANGE = "form-range";
const WITH_PREFIX = "with-prefix";
const WITH_SUFFIX = "with-suffix";
const WITH_CLEAR_BUTTON = "with-clear-button";

/** 
 * Describe the list of native events used by cjs-input component 
 */
type InputCjsNativeEventType = "change" | "input" | "cut" | "copy" | "paste" | "focus";

/**
 * Class representing a component with an input text
 */
@CustomElement({
    name: "cjs-input",
    style: require("./input.scss"),
    template: require("./input.html")
})
export class CjsInput<InputValueType extends AcceptInputValueType = AcceptInputValueType> extends WebComponent {

    /** Attributes options of editor */
    @TypedWebComponentOptions(defaultInputOptions)
    public readonly options!: WebComponentOptions<InputOptions<InputValueType>>;

    /** get the value of disabled property of options  */
    public get disabled(): boolean {
        return Boolean(getVOFValue(this.options.disabled));
    }

    /** components */
    @TypedHtmlElements()
    public readonly components!: {
        input: HTMLInputElement,
        inputTextarea: HTMLTextAreaElement,
        inputPrefix: HTMLSpanElement,
        inputSuffix: HTMLSpanElement,
        clearButton: HTMLButtonElement
    };

    /** Form group div where input is wrapped if a prefix/suffix or clear button is present */
    public formGroup?: HTMLDivElement;

    /** Form floating div where input is wrapped if floating is enable */
    public formFloating?: HTMLDivElement;

    /** Value of input */
    protected _value?: InputValueType;

    /** Get input value */
    public get value(): InputValueType | undefined {
        const inputValue = this.input.value;

        if (inputValue === "" && this._value === undefined) {
            this._value = undefined;
        } else if (this.isNumberInput()) {
            this._value = isNumeric(inputValue) ? Number(inputValue) as InputValueType : undefined;
        } else {
            this._value = inputValue as InputValueType;
        }

        this.options.setWithoutRefresh({ value: this._value });
        return this._value;
    }

    /** Set input value */
    public set value(inputValue: InputValueType | undefined) {
        this.options.setWithoutRefresh({ value: inputValue });
        this._refreshValue();
    }

    /** Get input based on option */
    public get input(): HTMLInputElement | HTMLTextAreaElement {
        return this.isTextareaInput() ? this.components.inputTextarea : this.components.input;
    }

    /**
     * Dispatch events
     */
    @Event(NoBubblesEventInit)
    public changeEvent!: EventHandler<Event | undefined>;
    @Event(NoBubblesEventInit)
    public inputEvent!: EventHandler<KeyboardEvent>;
    @Event(NoBubblesEventInit)
    public cutEvent!: EventHandler<ClipboardEvent>;
    @Event(NoBubblesEventInit)
    public copyEvent!: EventHandler<ClipboardEvent>;
    @Event(NoBubblesEventInit)
    public pasteEvent!: EventHandler<ClipboardEvent>;
    @Event(NoBubblesEventInit)
    public focusEvent!: EventHandler<Event | undefined>;

    /** When content of input is cleared */
    @Event(NoBubblesEventInit)
    public clearEvent!: EventHandler<InputClearEvent>;

    /** 
     * Associate native events with component custom events
     *
     * ! Only dispatch native "<input>" events here, or you should update initListeners() method !
     */
    private _events: Record<InputCjsNativeEventType, EventHandler<unknown>> = {
        "change": this.changeEvent,
        "input": this.inputEvent,
        "cut": this.cutEvent,
        "copy": this.copyEvent,
        "paste": this.pasteEvent,
        "focus": this.focusEvent
    };

    constructor(options?: Partial<InputOptions<InputValueType>>, customElementOptions?: OverrideCustomElementConfig) {
        super(customElementOptions);
        this.options.setWithoutRefresh(options);
    }

    /**
     * Initialize components, input components and callbacks after the element is added to the dom and _components is ready
     */
    public override init(): void {
        this._setInputsName();
        this._initListeners();
    }

    /** Refresh component display */
    public override refresh(): void {
        this._refreshTextarea();
        this._refreshInputType();
        // Refresh range before setting value, as the value can be capped on range input by the min and max boundaries
        this._refreshRangeAttributes();
        // Refresh components with options
        this._refreshValue();
        this._refreshPlaceholder();
        this._refreshFormControl();
        this._refreshReadonly();
        this._refreshWidth();
        this._refreshDisabled();
        this._refreshSize();
        this._refreshFloatingLabel();// Refresh floating label before input
        this.updateInputPrefixAndSuffix();

    }

    /**
     * Redirect focus on input
     */
    public override focus(options?: FocusOptions): void {
        this.input.focus(options);
    }

    /**
     * Calls select on the input if available
     */
    public select(): void {
        if (typeof this.input["select"] === "function") {
            this.input["select"]();
        }
    }

    /**
     * Get caret position (only works for text editors), returns -1 otherwise
     */
    public get selectionStart(): number {
        if (typeof this.input["selectionStart"] === "number") {
            return this.input["selectionStart"];
        }
        return -1;
    }

    /**
     * Dispatch event on input
     */
    public dispatchEventOnInput(event: Event): boolean {
        return this.input.dispatchEvent(event);
    }


    /** Init listeners of input */
    protected _initListeners(): void {
        // Function to dispatch 
        const dispatchEventByKey = (eventKey: string, event: unknown): void => {
            // Cast to avoid any type
            this._events[eventKey as keyof typeof this._events].emit(event);
        };

        Object.keys(this._events).forEach(eventKey => {

            let inputEventCallBack: (e: Event) => void;
            let textareaEventCallBack: (e: Event) => void;

            const onEvent = (e: Event, isTextarea = false): void => {
                if (!isTextarea) {
                    if (this.options.textarea.enable) { return; }
                } else {
                    if (!this.options.textarea.enable) { return; }
                }
                dispatchEventByKey(eventKey, e);
            };

            if (eventKey === "change") {
                inputEventCallBack = (e: Event): void => {
                    onEvent(e);
                    this.updateInputPrefixAndSuffix();
                };
                textareaEventCallBack = (e: Event): void => {
                    onEvent(e, true);
                    this.updateInputPrefixAndSuffix();
                };
            } else {
                inputEventCallBack = (e: Event): void => { onEvent(e); };
                textareaEventCallBack = (e: Event): void => { onEvent(e, true); };
            }

            // Init listeners on input, then dispatch from inputCjs component
            this.components.input.addEventListener(eventKey, (e) => inputEventCallBack(e));

            // Init listeners on textarea, then dispatch from inputCjs component
            this.components.inputTextarea.addEventListener(eventKey, (e) => textareaEventCallBack(e));
        });

        const captureEvent = (e: KeyboardEvent): void => {
            if (this.options.doNotCaptureKeyEvents === true ||
                Array.isArray(this.options.doNotCaptureKeyEvents) && this.options.doNotCaptureKeyEvents.includes(e.key as unknown as KeyboardInput)) {
                return;
            }
            // capture event to avoid unexpected event trigger outside in the application
            e.stopPropagation();
        };
        this.addEventListener("keydown", captureEvent);
        this.addEventListener("keypress", captureEvent);
        this.addEventListener("keyup", captureEvent);

        // Add clear event listener
        this.components.clearButton.addEventListener("click", () => {

            // Only emit events if value isn't undefined (<=> value already empty)
            if (this.value !== undefined) {
                const valueCleared = this.value;
                this.value = undefined;

                // Emit clear event after value reset
                this.clearEvent.emit({ valueCleared });

                // Emit value has changed (cleared)
                this.changeEvent.emit(undefined);
            }
        });
    }

    /**
     * Set input placeholder text value
     */
    protected _refreshPlaceholder(): void {
        toggleAttribute(this.input, this.options.placeholderText != null, "placeholder", this.options.placeholderText!);// Placeholder is defined if we check not null
    }

    /** Refresh value of input */
    protected _refreshValue(): void {
        this._value = this.options.value;
        this.input.value = String(this._value != null ? this._value : "");
    }

    /**
     * Update input prefix and suffix
     */
    public updateInputPrefixAndSuffix(): void {

        // Remove prefix classes
        this.classList.remove(WITH_PREFIX, WITH_SUFFIX);

        // Remove prefix if not in options
        if (!this.options.inputPrefixText || this.options.inputPrefixText === "") {
            this.components.inputPrefix.remove();
        } else {// Add prefix
            setHTMLElementContent(this.components.inputPrefix, this.options.inputPrefixText);
            this.classList.add(WITH_PREFIX);
        }

        // Remove suffix if not in options
        if (!this.options.inputSuffixText || this.options.inputSuffixText === "") {
            this.components.inputSuffix.remove();
        } else {// Add suffix
            setHTMLElementContent(this.components.inputSuffix, this.options.inputSuffixText);
            this.classList.add(WITH_SUFFIX);
        }

        // Check if clear button should be displayed
        if (!this._isClearButtonEnable()) {
            this.components.clearButton.remove();
        } else {
            this.input.after(this.components.clearButton);// Append clearBtn after input displayed
            this.classList.add(WITH_CLEAR_BUTTON);
        }

        // Reset formGroup
        if (this.formGroup) {
            this.formGroup.remove();
            this.formGroup = undefined;
        }

        // Check if input-group should be created
        if (this.options.inputPrefixText || this.options.inputSuffixText || this._isClearButtonEnable()) {

            // Create form group
            this.formGroup = createHtmlElement("div", {
                cssClass: ["input-group", ALIGN_ITEMS.CENTER],// Align items center, align input with clear btn
                innerHTML: [
                    this.input
                ]
            });

            // Add components after/before input component
            if (this.options.inputPrefixText) {
                this.input.before(this.components.inputPrefix);
            }
            if (this.options.inputSuffixText) {
                this.input.after(this.components.inputSuffix);
            }
            if (this._isClearButtonEnable()) {
                this.input.after(this.components.clearButton);
            }
            this.append(this.formGroup);

        } else if (!this.formFloating) {
            if (!findElementByRef(this, "input")) {// Only append input if not already append in this
                // If no suffix, prefix, or clear button to display, append input in this content
                this.append(this.input);
            }

        }

    }

    /**
     * Update floatingLabel of input
     */
    protected _refreshFloatingLabel(): void {

        // Reset formFloating
        if (this.formFloating) {
            this.formFloating.remove();
            this.formFloating = undefined;
        }
        if (!this.options.floatingLabel.enable) { return; }

        // Only create formFloating if isn't already initialized
        this.formFloating = createHtmlElement("div", {
            cssClass: ["form-floating", FLEX_ITEM_AUTO],
            innerHTML: this.input
        });

        // Add components after/before input component
        if (this.options.inputPrefixText) {
            this.input.before(this.components.inputPrefix);
        }
        if (this.options.inputSuffixText) {
            this.input.after(this.components.inputSuffix);
        }
        if (this._isClearButtonEnable()) {
            this.input.after(this.components.clearButton);
        }

        const labelId = "inputLabelId-" + this.componentNumber;
        const label = createHtmlElement("label", {
            innerHTML: this.options.floatingLabel.text,
            attributes: {
                for: labelId
            }
        });
        this.input.after(label);

        this.append(this.formFloating);

    }

    /**
     * Update readonly display, based on options
     */
    protected _refreshReadonly(): void {

        // Set input to readonly
        toggleAttribute(this.input, this.options.readonly.enable, READONLY_ATTRIBUTE_KEY, "true");

        // Add or remove plainText
        toggleClass(this.input, this.options.readonly.plainText, READONLY_PLAIN_TEXT);
    }

    /** Update size of input */
    protected _refreshSize(): void {

        // Switch with value defined in size
        switch (this.options.size) {
            case InputSize.LARGE:
                this.input.classList.remove("form-control-sm");
                this.input.classList.add("form-control-lg");
                break;
            case InputSize.SMALL:
                this.input.classList.add("form-control-sm");
                this.input.classList.remove("form-control-lg");
                break;
            case InputSize.NORMAL:
            default:
                this.input.classList.remove("form-control-lg", "form-control-sm");
                break;
        }
    }

    /**
     * Update form-control display, based on options
     */
    protected _refreshFormControl(): void {
        // Range input doesn't look pretty with form-control
        toggleClass(this.input, this.options.enableFormControl && this.options.inputType !== "range", FORM_CONTROL);
    }

    /**
     * Update disabled, based on options
     */
    public _refreshDisabled(): void {
        this.input.disabled = this.disabled;
    }

    /**
     * Update textarea display, based on options
     */
    protected _refreshTextarea(): void {

        // Update display
        toggleVisible(this.components.inputTextarea, this.options.textarea.enable);
        toggleVisible(this.components.input, !this.options.textarea.enable);

        // Textarea
        if (this.options.textarea.enable) {

            // Remove input from DOM
            this.components.input.remove();
            // Add textarea in DOM
            this.components.inputPrefix.after(this.components.inputTextarea);

            // Set rows count
            if (isNumber(this.options.textarea.rows)) {
                this.input.setAttribute("rows", String(this.options.textarea.rows));
            }

        } else {// Input
            // Add input in DOM
            this.components.inputPrefix.after(this.components.input);
            // Remove textarea from DOM
            this.components.inputTextarea.remove();
        }
    }

    /**
     * Update width of input, based on options
     */
    protected _refreshWidth(): void {
        if (this.options.width) {
            this.input.style.width = this.options.width;
        }
    }

    /**
     * Update input type, based on options
     */
    protected _refreshInputType(): void {
        this.input.setAttribute("type", this.options.inputType);

        toggleClass(this.input, this.options.inputType === "color", FORM_CONTROL_COLOR);
        toggleClass(this.input, this.options.inputType === "range", FORM_RANGE);
    }

    /**
     * Toggle input attributes 
     * @returns 
     */
    protected _refreshRangeAttributes(): void {
        const check: boolean = this.isNumberInput() && this.options.rangeOptions != null;
        toggleAttribute(this.input, check && this.options.rangeOptions?.min != null, "min", "" + this.options.rangeOptions?.min);
        toggleAttribute(this.input, check && this.options.rangeOptions?.max != null, "max", "" + this.options.rangeOptions?.max);
        const step = this.options.rangeOptions?.step ?? (this.options.useFloat ? "any" : undefined);
        toggleAttribute(this.input, check && step !== undefined, "step", String(step));

    }

    /** @returns true if clear button is enable */
    private _isClearButtonEnable(): boolean {
        return Boolean(this.options.enableClearButton) && !this.disabled && this.value != null;
    }

    /**
     * Set name of inputs
     * (name="input(\d)" to prevent chrome from suggesting password)
     */
    protected _setInputsName(): void {
        this.components.input.setAttribute("name", this.options.inputName ?? "input_" + this.componentNumber);
        this.components.inputTextarea.setAttribute("name", this.options.inputName ?? "textarea_" + this.componentNumber);
    }

    /**
     * @returns input is in "Number" mode, else input is in "String" mode
     */
    public isNumberInput(): boolean {
        return this.options.inputType === "number" || this.options.inputType === "range";
    }

    /**
     * @returns input is a "Textarea" div, else input is a "input" div
     */
    public isTextareaInput(): boolean {
        return this.options.textarea.enable;
    }

}